import React, { useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import { viewerS3Url } from '../../utils/config';

export interface SlideViewProps {
    slideId: string;
}

type ImageState = 'primary' | 'tiles' | 'error';

export const SlideView: React.FC<SlideViewProps> = ({ slideId }) => {
    const [imgState, setImgState] = useState<ImageState>('primary');

    const tiles: [number, number][] = [[0, 0], [0, 1], [1, 0], [1, 1]];
    const failedTilesCount = useRef<number>(0);
    const totalTiles = 4;

    const handlePrimaryError = () => setImgState('tiles');
    const handleTileError = () => setImgState('error');

    const onSingleTileError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        e.currentTarget.style.visibility = 'hidden';
        failedTilesCount.current += 1;
        if (failedTilesCount.current === totalTiles) handleTileError();
    };

    const getTileUrl = (x: number, y: number) => {
        return `${viewerS3Url}/${slideId}/{z}/{x}/{y}.png`
            .replace('{z}', '1')
            .replace('{x}', x.toString())
            .replace('{y}', y.toString());
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 512,
                aspectRatio: '1 / 1',
                margin: '0 auto',
                bgcolor: '#F5F5F5',
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                userSelect: 'none',
                WebkitUserDrag: 'none'
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#F5F5F5'
                }}
            >
                {imgState === 'primary' && (
                    <Box
                        component="img"
                        src={`${viewerS3Url}/${slideId}/thumbnail.webp`}
                        onError={handlePrimaryError}
                        alt="Heatmap"
                        draggable={false}
                        sx={{ width: 'fit-content', height: 'fit-content', objectFit: 'cover' }}
                    />
                )}

                {imgState === 'tiles' && (
                    <Box sx={{
                        width: '100%',
                        height: '100%',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gridTemplateRows: '1fr 1fr'
                    }}>
                        {tiles.map((i, index) => (
                            <Box
                                key={index}
                                component="img"
                                src={getTileUrl(i[0], i[1])}
                                onError={onSingleTileError}
                                draggable={false}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            {/* Error Overlay */}
            {imgState === 'error' && (
                <Box sx={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#111',
                    color: 'rgba(255,255,255,0.6)',
                    zIndex: 5
                }}>
                    <ImageNotSupportedIcon sx={{ fontSize: { xs: 48, md: 64 }, mb: 2, opacity: 0.5 }} />
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Slide View Unavailable</Typography>
                    <Typography variant="caption" align="center" sx={{ px: 2 }}>
                        Source image and tiles were not found.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};